/* oxlint-disable no-console */
import * as R from 'remeda';
import { isDevFeatureEnabled } from '../constants/dev';

export type BandwidthSummary = {
  bytesPerSecondMean: number | null;
  ttfbMeanMs: number | null;
};

type Options = {
  windowMs?: number;
  allowedInitiators?: Array<PerformanceResourceTiming['initiatorType']>;
  allowedHostnames?: string[];
  minBytes?: number;
};

type Sample = { start: number; end: number; size: number; ttfb: number };

let observer: PerformanceObserver | null = null;
let samples: Sample[] = [];
let config: Required<Options> = {
  windowMs: 60000,
  allowedInitiators: ['fetch', 'xmlhttprequest'],
  allowedHostnames: [location.hostname],
  minBytes: 1024,
};

const prune = (now = performance.now()) => {
  const cutoff = now - config.windowMs;
  samples = samples.filter(s => s.end > cutoff);
};

export const roundOrNull = (v: number | undefined) =>
  v !== undefined && Number.isFinite(v) ? Math.round(v) : null;

const isAllowed = (entry: PerformanceResourceTiming) => {
  if (!config.allowedInitiators.includes(entry.initiatorType)) return false;

  try {
    const url = new URL(entry.name);

    if (!config.allowedHostnames.includes(url.hostname)) return false;
  } catch {
    return false;
  }

  if (!entry.transferSize || entry.transferSize < config.minBytes) return false;
  if (entry.responseEnd <= entry.responseStart) return false;
  if (entry.requestStart <= 0) return false;

  return true;
};

export const startBandwidthObserver = (options?: Options) => {
  if (observer) return () => {};

  config = { ...config, ...(options || {}) };

  observer = new PerformanceObserver(list => {
    const now = performance.now();

    list.getEntries().forEach(entry => {
      if (entry.entryType !== 'resource') return;

      const resourceEntry = entry as PerformanceResourceTiming;
      if (!isAllowed(resourceEntry)) return;

      // We rely on isAllowed to check responseStart < responseEnd and transferSize >= minBytes
      samples.push({
        start: resourceEntry.responseStart,
        end: resourceEntry.responseEnd,
        size: resourceEntry.transferSize,
        ttfb: resourceEntry.responseStart - resourceEntry.requestStart,
      });
    });

    prune(now);
  });

  observer.observe({ type: 'resource', buffered: true });

  return () => {
    observer?.disconnect();
    observer = null;
    samples = [];
  };
};

export const getBandwidthSummary = (): BandwidthSummary => {
  prune();

  if (samples.length === 0) {
    return { bytesPerSecondMean: null, ttfbMeanMs: null };
  }

  const totalBytes = R.pipe(
    samples,
    R.map(s => s.size),
    R.sum()
  );

  const bytesPerSecond = R.pipe(
    samples,
    R.sortBy(s => s.start),
    sortedSamples =>
      R.reduce(
        sortedSamples,
        (acc, s) => {
          if (s.start < acc.currentEnd) {
            // Overlap: extend the current interval if needed
            return { ...acc, currentEnd: Math.max(acc.currentEnd, s.end) };
          }

          // No overlap: commit the previous interval and start a new one
          return {
            totalDuration:
              acc.totalDuration + (acc.currentEnd - acc.currentStart),
            currentStart: s.start,
            currentEnd: s.end,
          };
        },
        {
          totalDuration: 0,
          currentStart: sortedSamples[0]?.start ?? 0,
          currentEnd: sortedSamples[0]?.end ?? 0,
        }
      ),

    ({ totalDuration, currentStart, currentEnd }) => {
      const finalDuration = totalDuration + (currentEnd - currentStart);
      if (isDevFeatureEnabled('console-log'))
        console.log('Download Speed: total duration', finalDuration);
      return finalDuration;
    },
    finalDuration =>
      finalDuration > 0 ? (totalBytes / finalDuration) * 1000 : 0
  );

  const ttfbMean = R.pipe(
    samples,
    R.map(s => s.ttfb),
    R.mean()
  );

  if (isDevFeatureEnabled('console-log')) {
    console.log('Download Speed: samples', samples);
    console.log('Download Speed: total bytes', totalBytes);
    console.log('Download Speed: average in bytes per second', bytesPerSecond);
    console.log('Download Speed: average TTFB in ms', ttfbMean);
  }

  return {
    bytesPerSecondMean: roundOrNull(bytesPerSecond),
    ttfbMeanMs: roundOrNull(ttfbMean),
  };
};
